/**
 * Static explanation pages.
 *
 * They share a shape: a short intro, a few sections, and an explicit statement of
 * what the tool does and does not do.
 */

import { useTranslation } from 'react-i18next'

import { PageHeader } from '../components/layout/page-header'
import { PromotionLinkCard } from '../components/referral/promotion-link'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { useCurrentUser } from '../hooks/use-auth'

export function AboutPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useCurrentUser()

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[{ label: t('nav.home'), to: '/' }, { label: t('nav.about') }]}
        title={t('about.title')}
        description={t('about.intro')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('about.problemTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">{t('about.problemBody')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('about.approachTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">{t('about.approachBody')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('about.scopeTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">{t('about.scopeBody')}</p>
        </CardContent>
      </Card>

      {/* Second promotion entry point, next to the explanation of the product. */}
      {isAuthenticated ? <PromotionLinkCard /> : null}
    </div>
  )
}

export function GuidePage() {
  const { t } = useTranslation()

  const organizerSteps = [
    t('guide.organizerStep1'),
    t('guide.organizerStep2'),
    t('guide.organizerStep3'),
    t('guide.organizerStep4'),
  ]
  const participantSteps = [
    t('guide.participantStep1'),
    t('guide.participantStep2'),
    t('guide.participantStep3'),
  ]

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[{ label: t('nav.home'), to: '/' }, { label: t('nav.guide') }]}
        title={t('guide.title')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('guide.organizerTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
            {organizerSteps.map((step) => (
              <li key={step} className="text-muted-foreground">
                {step}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('guide.participantTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
            {participantSteps.map((step) => (
              <li key={step} className="text-muted-foreground">
                {step}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('guide.privacyTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">{t('guide.privacyBody')}</p>
        </CardContent>
      </Card>
    </div>
  )
}
